import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiListEnvelop } from '@mkp/shared/models';
import { getAuthDomain } from '@mkp/shared/util-auth';
import { ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment';
import { CLIENT_ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment/client';
import { QueryOptions } from '@mkp/shared/util-pagination';
import { UserDto, UserPatch } from './user.dto';
import { ResetPasswordBody, ResetPasswordDto } from './reset-password.dto';
import { UserMapper } from './user.mapper';
import { ResourceService } from '@mkp/shared/data-access';
import { UpdateUser, User } from '@mkp/user/models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UserApi extends ResourceService<UserDto> {
  override readonly httpClient: HttpClient;
  protected readonly userMapper: UserMapper;
  private readonly auth = inject(CLIENT_ENVIRONMENT_PROVIDER).auth;

  constructor() {
    const httpClient = inject(HttpClient);
    const userMapper = inject(UserMapper);
    const env = inject(ENVIRONMENT_PROVIDER);

    super(httpClient, env.api.core, 'user', userMapper);

    this.httpClient = httpClient;
    this.userMapper = userMapper;
  }

  resetPassword(email: string): Observable<ResetPasswordDto> {
    const url = `https://${getAuthDomain(this.auth)}/dbconnections/change_password`;
    const body: ResetPasswordBody = {
      email,
      client_id: this.auth.clientId,
      connection: 'Username-Password-Authentication',
    };

    return this.httpClient.post<ResetPasswordDto>(url, body);
  }

  resetMfa(userId: string): Observable<void> {
    const url = `${this.url}/user/${userId}/reset-mfa`;
    return this.httpClient.post<void>(url, {});
  }

  // I don't use "update" because I don't want to use Partial<User>, I want a customized UpdateUser object here
  patch(user: UpdateUser, maxRetry = 3): Observable<User> {
    const body: UserPatch = this.userMapper.toJson(user);
    return this.patchValue({ id: user.id, ...body }, { maxRetry });
  }

  // for some reason the resource service doesn't map the result
  listAndMap(queryOptions?: QueryOptions): Observable<ApiListEnvelop<User>> {
    return super.list(queryOptions).pipe(
      map((envelop) => ({
        ...envelop,
        results: [],
        _embedded: {
          ...envelop._embedded,
          results: envelop._embedded?.results.map(this.userMapper.fromJson) ?? [],
        },
      }))
    );
  }

  // read has wrong typing in resource service
  readAndMap(id: string): Observable<User> {
    return super.read(id) as unknown as Observable<User>;
  }
}
